import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import axios from "axios";
import {
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeftRight,
  Search,
  Filter,
  Download,
  CreditCard,
  Building2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const transactionTypes = [
  { value: "all", label: "All Types" },
  { value: "transfer", label: "Transfers" },
  { value: "deposit", label: "Deposits" },
  { value: "withdrawal", label: "Withdrawals" },
  { value: "fx_conversion", label: "FX Conversions" },
  { value: "supplier_payment", label: "Supplier Payments" },
  { value: "credit_advance", label: "Credit Advances" },
];

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const params = filter !== "all" ? `?transaction_type=${filter}` : "";
        const response = await axios.get(`${API}/transactions${params}`, { withCredentials: true });
        setTransactions(response.data);
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [filter]);

  const getTransactionIcon = (type) => {
    switch (type) {
      case "deposit":
      case "credit_advance":
        return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
      case "withdrawal":
      case "transfer":
        return <ArrowUpRight className="h-4 w-4 text-red-600" />;
      case "fx_conversion":
        return <ArrowLeftRight className="h-4 w-4 text-blue-600" />;
      case "supplier_payment":
        return <Building2 className="h-4 w-4 text-purple-600" />;
      default:
        return <CreditCard className="h-4 w-4 text-slate-600" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case "deposit":
      case "credit_advance":
        return "bg-green-100";
      case "withdrawal":
      case "transfer":
        return "bg-red-100";
      case "fx_conversion":
        return "bg-blue-100";
      case "supplier_payment":
        return "bg-purple-100";
      default:
        return "bg-slate-100";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredTransactions = transactions.filter((tx) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      tx.recipient_name?.toLowerCase().includes(searchLower) ||
      tx.note?.toLowerCase().includes(searchLower) ||
      tx.transaction_type.toLowerCase().includes(searchLower) ||
      tx.currency.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="transactions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Transactions</h1>
            <p className="text-slate-600 mt-1">View all your transaction history</p>
          </div>
          <Button variant="outline" className="rounded-full" data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Search transactions..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full sm:w-48 h-11 rounded-xl" data-testid="filter-select">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {transactionTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Transactions List */}
        <Card className="border-slate-200" data-testid="transactions-list">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              {filteredTransactions.length} Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredTransactions.length > 0 ? (
              <div className="space-y-3">
                {filteredTransactions.map((tx) => (
                  <div
                    key={tx.transaction_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    data-testid={`tx-${tx.transaction_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-full ${getTransactionColor(tx.transaction_type)} flex items-center justify-center`}>
                        {getTransactionIcon(tx.transaction_type)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {tx.recipient_name || tx.transaction_type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {tx.transaction_type.replace(/_/g, " ")}
                          </Badge>
                          <span className="text-xs text-slate-500">{formatDate(tx.created_at)}</span>
                        </div>
                        {tx.note && (
                          <p className="text-xs text-slate-500 mt-1">{tx.note}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${
                        tx.transaction_type === "deposit" || tx.transaction_type === "credit_advance"
                          ? "text-green-600"
                          : "text-slate-900"
                      }`}>
                        {tx.transaction_type === "deposit" || tx.transaction_type === "credit_advance" ? "+" : "-"}
                        {tx.currency} {tx.amount.toLocaleString()}
                      </p>
                      <Badge
                        className={`mt-1 ${
                          tx.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : tx.status === "pending"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {tx.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No transactions found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
